import type { SiteTalentspaceContent } from '../types';
// Header/footer content of the edunet sites, copied 1:1 from their main sites:
// edunet.works (sid edunet), edunet.in (sid edunetin), edunet.ke (sid edunetke) and
// edunet.co.ma (sid edunetma). All run
// the same site theme, only the content differs. A portal language the main site lacks falls
// back to English.

export const EDUNET_SITE = 'https://edunet.works';

export const edunetLanguages = ['en', 'de', 'uk', 'fr', 'es', 'ar'] as const;

export type EdunetLanguage = typeof edunetLanguages[number];

interface EdunetCompanyRow {
  label: string
  value: string
  href?: string
}

// Texts marked optional exist on some of the sites only
interface EdunetTexts {
  nav: {
    welcome: string
    educationalNetwork: string
    jobPlacement: string
    learnGerman: string
    talentPool: string
    talents?: string
    aboutUs: string
    contact: string
  }
  pages: string
  services?: string
  partners?: string
  companyInformation: string
  description: string
  address?: string
  germanCourses?: string
  jobPlacementService?: string // services column label when it differs from the menu
  register?: string
  organisation?: string
  managingDirector?: string
  companyRegister?: string
  court?: string
  vatId?: string
  companyRows?: EdunetCompanyRow[] // own rows of the last column instead of the Edunet GmbH details
  rights: string
  privacy: string
  legalNotice?: string
}

export const edunetTexts: Record<EdunetLanguage, EdunetTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      educationalNetwork: 'Educational Network',
      jobPlacement: 'Job Placement',
      learnGerman: 'Learn German',
      talentPool: 'Talent Pool',
      talents: 'Talents',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Company information',
    description: 'Connecting international skilled workers with German employers through language training, career preparation, and job placement.',
    germanCourses: 'German Language Courses',
    register: 'Register, it\'s free',
    managingDirector: 'Managing Director',
    companyRegister: 'Company Register',
    court: 'Court',
    vatId: 'VAT ID',
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
    legalNotice: 'Legal Notice',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      educationalNetwork: 'Bildungsnetzwerk',
      jobPlacement: 'Jobvermittlung',
      learnGerman: 'Deutsch lernen',
      talentPool: 'Talent Pool',
      talents: 'Talente',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    pages: 'Seiten',
    services: 'Leistungen',
    companyInformation: 'Unternehmensangaben',
    description: 'Wir verbinden internationale Fachkräfte mit deutschen Arbeitgebern – durch Sprachausbildung, Karrierevorbereitung und Jobvermittlung.',
    germanCourses: 'Deutschkurse',
    register: 'Kostenlos registrieren',
    managingDirector: 'Geschäftsführerin',
    companyRegister: 'Handelsregister',
    court: 'Registergericht',
    vatId: 'USt-IdNr.',
    rights: 'Alle Rechte vorbehalten.',
    privacy: 'Datenschutz',
    legalNotice: 'Impressum',
  },
  uk: {
    nav: {
      welcome: 'Ласкаво просимо',
      educationalNetwork: 'Освітня мережа',
      jobPlacement: 'Працевлаштування',
      learnGerman: 'Вивчати німецьку',
      talentPool: 'Talent Pool',
      talents: 'Таланти',
      aboutUs: 'Про нас',
      contact: 'Контакт',
    },
    pages: 'Сторінки',
    services: 'Послуги',
    companyInformation: 'Дані компанії',
    description: 'Ми поєднуємо міжнародних фахівців з німецькими роботодавцями через мовну підготовку, підготовку до кар\'єри та послуги з працевлаштування.',
    germanCourses: 'Курси німецької мови',
    register: 'Зареєструватися безкоштовно',
    managingDirector: 'Генеральний директор',
    companyRegister: 'Торговий реєстр',
    court: 'Суд',
    vatId: 'ПДВ ID',
    rights: 'Усі права захищені.',
    privacy: 'Політика конфіденційності',
    legalNotice: 'Правова інформація',
  },
  fr: {
    nav: {
      welcome: 'Accueil',
      educationalNetwork: 'Réseau éducatif',
      jobPlacement: 'Placement professionnel',
      learnGerman: 'Apprendre l\'allemand',
      talentPool: 'Talent Pool',
      talents: 'Talents',
      aboutUs: 'À propos',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Informations société',
    description: 'Nous mettons en relation des professionnels internationaux avec des employeurs allemands grâce à la formation linguistique, la préparation à la carrière et le placement professionnel.',
    germanCourses: 'Cours d\'allemand',
    register: 'S\'inscrire gratuitement',
    managingDirector: 'Directrice générale',
    companyRegister: 'Registre du commerce',
    court: 'Tribunal',
    vatId: 'N° TVA',
    rights: 'Tous droits réservés.',
    privacy: 'Politique de confidentialité',
    legalNotice: 'Mentions légales',
  },
  es: {
    nav: {
      welcome: 'Bienvenida',
      educationalNetwork: 'Red educativa',
      jobPlacement: 'Inserción laboral',
      learnGerman: 'Aprender alemán',
      talentPool: 'Talent Pool',
      talents: 'Talentos',
      aboutUs: 'Sobre nosotros',
      contact: 'Contacto',
    },
    pages: 'Páginas',
    services: 'Servicios',
    companyInformation: 'Datos de la empresa',
    description: 'Conectamos a profesionales internacionales con empleadores alemanes a través de formación lingüística, preparación profesional y servicios de inserción laboral.',
    germanCourses: 'Cursos de alemán',
    register: 'Registrarse gratis',
    managingDirector: 'Directora general',
    companyRegister: 'Registro mercantil',
    court: 'Tribunal',
    vatId: 'NIF intracomunitario',
    rights: 'Todos los derechos reservados.',
    privacy: 'Política de privacidad',
    legalNotice: 'Aviso legal',
  },
  ar: {
    nav: {
      welcome: 'الرئيسية',
      educationalNetwork: 'الشبكة التعليمية',
      jobPlacement: 'التوظيف',
      learnGerman: 'تعلّم الألمانية',
      talentPool: 'Talent Pool',
      talents: 'المواهب',
      aboutUs: 'من نحن',
      contact: 'اتصل بنا',
    },
    pages: 'الصفحات',
    services: 'الخدمات',
    companyInformation: 'بيانات الشركة',
    // the Arabic main site shows no description under the company name
    description: '',
    germanCourses: 'دورات اللغة الألمانية',
    register: 'التسجيل مجاناً',
    managingDirector: 'المديرة العامة',
    companyRegister: 'السجل التجاري',
    court: 'المحكمة',
    vatId: 'الرقم الضريبي لضريبة القيمة المضافة',
    rights: 'جميع الحقوق محفوظة.',
    privacy: 'سياسة الخصوصية',
    legalNotice: 'إشعار قانوني',
  },
};

// edunet.in (sid edunetin): English, German and Hindi only
const edunetinTexts: Record<string, EdunetTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      educationalNetwork: 'Educational Network',
      jobPlacement: 'Job Placement',
      learnGerman: 'Learn German',
      talentPool: 'Talent Pool',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    pages: 'Pages',
    partners: 'Our Partners',
    companyInformation: 'Company Details',
    description: 'Edunet coordinates a professional network connecting skilled workers in India with language preparation, career guidance, and international opportunities.',
    address: 'Edunet GmbH · Luisenstr. 41, 10117 Berlin, Germany',
    organisation: 'Organisation',
    managingDirector: 'Managing Director',
    companyRegister: 'Company Register',
    court: 'Court',
    vatId: 'VAT ID',
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      educationalNetwork: 'Bildungsnetzwerk',
      jobPlacement: 'Jobvermittlung',
      learnGerman: 'Deutsch lernen',
      talentPool: 'Talent Pool',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    pages: 'Seiten',
    partners: 'Unsere Partner',
    companyInformation: 'Unternehmensangaben',
    description: 'Edunet koordiniert ein professionelles Netzwerk, das Fachkräfte in Indien mit Sprachvorbereitung, Karriereberatung und internationalen Chancen verbindet.',
    address: 'Edunet GmbH · Luisenstr. 41, 10117 Berlin, Germany',
    organisation: 'Organisation',
    managingDirector: 'Geschäftsführerin',
    companyRegister: 'Handelsregister',
    court: 'Registergericht',
    vatId: 'USt-IdNr.',
    rights: 'Alle Rechte vorbehalten.',
    privacy: 'Datenschutz',
  },
  hi: {
    nav: {
      welcome: 'स्वागत',
      educationalNetwork: 'शैक्षिक नेटवर्क',
      jobPlacement: 'जॉब प्लेसमेंट',
      learnGerman: 'जर्मन सीखें',
      talentPool: 'Talent Pool',
      aboutUs: 'हमारे बारे में',
      contact: 'संपर्क करें',
    },
    pages: 'पेज',
    partners: 'हमारे पार्टनर',
    companyInformation: 'कंपनी की जानकारी',
    description: 'Edunet एक प्रोफ़ेशनल नेटवर्क का समन्वय करता है, जो भारत के कुशल कामगारों को भाषा की तैयारी, करियर मार्गदर्शन और अंतरराष्ट्रीय अवसरों से जोड़ता है।',
    address: 'Edunet GmbH · Luisenstr. 41, 10117 बर्लिन, जर्मनी',
    organisation: 'संस्था',
    managingDirector: 'मैनेजिंग डायरेक्टर',
    companyRegister: 'कंपनी रजिस्टर',
    court: 'अदालत',
    vatId: 'VAT ID',
    rights: 'सर्वाधिकार सुरक्षित।',
    privacy: 'प्राइवेसी पॉलिसी',
  },
};

// edunet.ke (sid edunetke): English, German and Swahili
const kePlatform = { value: 'talentpool.edunet.ke', href: 'https://talentpool.edunet.ke/' };

const edunetkeTexts: Record<string, EdunetTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      educationalNetwork: 'Educational Network',
      jobPlacement: 'Job Placement',
      learnGerman: 'Learn German',
      talentPool: 'Talent Pool',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Connect',
    description: 'Connecting Kenyan skilled professionals with German language training, career preparation, and job placement opportunities in Germany.',
    address: 'Nairobi, Kenya',
    germanCourses: 'German Language Courses',
    jobPlacementService: 'Job Placement in Germany',
    register: 'Register free',
    companyRows: [
      { label: 'Organisation', value: 'Edunet Kenya' },
      { label: 'Location', value: 'Nairobi, Kenya' },
      { label: 'Part of', value: 'Edunet International Network' },
      { label: 'Platform', ...kePlatform },
    ],
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      educationalNetwork: 'Bildungsnetzwerk',
      jobPlacement: 'Jobvermittlung',
      learnGerman: 'Deutsch lernen',
      talentPool: 'Talent Pool',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    pages: 'Seiten',
    services: 'Leistungen',
    companyInformation: 'Kontakt',
    description: 'Wir verbinden qualifizierte Fachkräfte aus Kenia mit Deutschkursen, beruflicher Vorbereitung und Arbeitsmöglichkeiten in Deutschland.',
    address: 'Nairobi, Kenia',
    germanCourses: 'Deutschkurse',
    jobPlacementService: 'Arbeitsvermittlung in Deutschland',
    register: 'Kostenlos registrieren',
    companyRows: [
      { label: 'Organisation', value: 'Edunet Kenya' },
      { label: 'Standort', value: 'Nairobi, Kenya' },
      { label: 'Teil von', value: 'Edunet International Network' },
      { label: 'Plattform', ...kePlatform },
    ],
    rights: 'Alle Rechte vorbehalten.',
    privacy: 'Datenschutz',
  },
  sw: {
    nav: {
      welcome: 'Karibu',
      educationalNetwork: 'Mtandao wa Elimu',
      jobPlacement: 'Ajira',
      learnGerman: 'Jifunze Kijerumani',
      talentPool: 'Talent Pool',
      aboutUs: 'Kuhusu Sisi',
      contact: 'Wasiliana Nasi',
    },
    pages: 'Kurasa',
    services: 'Huduma',
    companyInformation: 'Mawasiliano',
    description: 'Tunawaunganisha wataalamu wenye ujuzi kutoka Kenya na mafunzo ya Kijerumani, maandalizi ya kazi na nafasi za ajira nchini Ujerumani.',
    address: 'Nairobi, Kenya',
    germanCourses: 'Kozi za Kijerumani',
    jobPlacementService: 'Ajira nchini Ujerumani',
    register: 'Jisajili bure',
    companyRows: [
      { label: 'Shirika', value: 'Edunet Kenya' },
      { label: 'Mahali', value: 'Nairobi, Kenya' },
      { label: 'Sehemu ya', value: 'Edunet International Network' },
      { label: 'Jukwaa', ...kePlatform },
    ],
    rights: 'Haki zote zimehifadhiwa.',
    privacy: 'Sera ya Faragha',
  },
};

// edunet.co.ma (sid edunetma): English, German, French and Arabic. Same columns as
// edunet.works, but the company rows run Managing Director → Company → Register …
const maCompanyRows = (labels: [string, string, string, string, string]): EdunetCompanyRow[] => [
  { label: labels[0], value: 'Jeniffer Muvai' },
  { label: labels[1], value: 'Edunet GmbH' },
  { label: labels[2], value: 'HRB 264074 B' },
  { label: labels[3], value: 'Amtsgericht Berlin Charlottenburg' },
  { label: labels[4], value: 'DE368496173' },
];

const edunetmaTexts: Record<string, EdunetTexts> = {
  en: {
    nav: {
      welcome: 'Welcome',
      educationalNetwork: 'Educational Network',
      jobPlacement: 'Job Placement',
      learnGerman: 'Learn German',
      talentPool: 'Talent Pool',
      aboutUs: 'About Us',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Company Details',
    description: 'Connecting Moroccan skilled workers with German employers through language training, career preparation, and job placement.',
    address: 'Luisenstr. 41, 10117 Berlin, Germany',
    germanCourses: 'German Language Courses',
    jobPlacementService: 'Job Placement in Germany',
    register: 'Register free',
    companyRows: maCompanyRows(['Managing Director', 'Company', 'Company Register', 'Court', 'VAT ID']),
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
  },
  de: {
    nav: {
      welcome: 'Willkommen',
      educationalNetwork: 'Bildungsnetzwerk',
      jobPlacement: 'Jobvermittlung',
      learnGerman: 'Deutsch lernen',
      talentPool: 'Talent Pool',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
    },
    pages: 'Seiten',
    services: 'Leistungen',
    companyInformation: 'Unternehmensangaben',
    description: 'Wir verbinden marokkanische Fachkräfte durch Sprachkurse, Karrierevorbereitung und Arbeitsvermittlung mit deutschen Arbeitgebern.',
    address: 'Luisenstr. 41, 10117 Berlin, Deutschland',
    germanCourses: 'Deutschkurse',
    jobPlacementService: 'Arbeitsvermittlung',
    register: 'Kostenlos registrieren',
    companyRows: maCompanyRows(['Geschäftsführerin', 'Unternehmen', 'Handelsregister', 'Registergericht', 'USt-IdNr.']),
    rights: 'All rights reserved.',
    privacy: 'Datenschutz',
  },
  fr: {
    nav: {
      welcome: 'Accueil',
      educationalNetwork: 'Réseau éducatif',
      jobPlacement: 'Placement professionnel',
      learnGerman: "Apprendre l'allemand",
      talentPool: 'Talent Pool',
      aboutUs: 'À propos',
      contact: 'Contact',
    },
    pages: 'Pages',
    services: 'Services',
    companyInformation: 'Informations sur l’entreprise',
    description: 'Nous mettons en relation des professionnels qualifiés marocains avec des employeurs allemands grâce à la formation linguistique, à la préparation professionnelle et au placement professionnel.',
    address: 'Luisenstr. 41, 10117 Berlin, Allemagne',
    germanCourses: "Cours d'allemand",
    register: 'Inscription gratuite',
    companyRows: maCompanyRows(['Directrice générale', 'Société', 'Registre du commerce', 'Tribunal', 'N° de TVA']),
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
  },
  ar: {
    nav: {
      welcome: 'الرئيسية',
      educationalNetwork: 'الشبكة التعليمية',
      jobPlacement: 'التوظيف',
      learnGerman: 'تعلّم الألمانية',
      talentPool: 'Talent Pool',
      aboutUs: 'من نحن',
      contact: 'اتصل بنا',
    },
    pages: 'الصفحات',
    services: 'الخدمات',
    companyInformation: 'بيانات الشركة',
    description: 'نربط الكفاءات المغربية بأصحاب العمل في ألمانيا من خلال التدريب اللغوي، والاستعداد المهني، وخدمات التوظيف.',
    address: 'Luisenstr. 41، 10117 برلين، ألمانيا',
    germanCourses: 'دورات اللغة الألمانية',
    register: 'سجّل مجانًا',
    companyRows: maCompanyRows(['المديرة التنفيذية', 'الشركة', 'السجل التجاري', 'المحكمة المختصة', 'رقم ضريبة القيمة المضافة']),
    rights: 'All rights reserved.',
    privacy: 'Privacy policy',
  },
};

interface EdunetLink {
  label: string
  href: string
}

// What differs between the edunet sites
export interface EdunetBrand {
  site: string
  languages: readonly string[]
  texts: Record<string, EdunetTexts>
  logo: string
  footerTitle: string
  address: string // when the texts have no translated address
  email: string
  whatsapp: string
  copyrightName: string
  privacyPath: string
  phone?: EdunetLink
  website?: EdunetLink
  linkedin?: string
  hasTalentsPage: boolean // "Talents" in the menu
  partners?: EdunetLink[] // "Our Partners" column; without it the "Services" column
  germanCoursesFirst?: boolean // order in the "Services" column
  linkColor?: string // the main site's link colour where it differs from the portal colour
  iconColor?: string // the main site's footer icon colour where it differs from the portal colour
  whatsappLabel?: string // the WhatsApp row shows this (the number) instead of "WhatsApp"
  hasLegalNotice: boolean
}

const edunetBrands: Record<string, EdunetBrand> = {
  edunet: {
    site: EDUNET_SITE,
    languages: edunetLanguages,
    texts: edunetTexts,
    // eslint-disable-next-line global-require
    logo: require('@/assets/img/logo/logo-work-edunet.png'),
    footerTitle: 'Edunet GmbH',
    address: 'Luisenstr. 41, 10117 Berlin, Germany',
    email: 'info@edunet.works',
    whatsapp: 'https://wa.me/4915563379350',
    copyrightName: 'Edunet GmbH',
    privacyPath: 'content/privacy-policy',
    phone: { label: '+49 15563 379350', href: 'tel:+4915563379350' },
    linkedin: 'https://www.linkedin.com/company/edunet-gmbh',
    hasTalentsPage: true,
    hasLegalNotice: true,
  },
  edunetin: {
    site: 'https://edunet.in',
    languages: ['en', 'de', 'hi'],
    texts: edunetinTexts,
    // eslint-disable-next-line global-require
    logo: require('@/assets/img/logo/logo-edunetin.png'),
    footerTitle: 'Edunet India',
    address: 'Edunet GmbH · Luisenstr. 41, 10117 Berlin, Germany',
    email: 'info@edunet.in',
    whatsapp: 'https://wa.me/4915563379350',
    copyrightName: 'Edunet GmbH',
    privacyPath: 'content/privacy-policy',
    website: { label: 'edunet.in', href: 'https://edunet.in' },
    hasTalentsPage: false,
    partners: [
      { label: 'AJEETS', href: 'https://ajeets.com' },
      { label: 'Transcura GmbH', href: 'https://transcura.de' },
      { label: 'Jobnet.AG', href: 'https://jobnet.international' },
    ],
    hasLegalNotice: false,
  },
  edunetke: {
    site: 'https://edunet.ke',
    languages: ['en', 'de', 'sw'],
    texts: edunetkeTexts,
    // eslint-disable-next-line global-require
    logo: require('@/assets/img/logo/logo-edunetke.png'),
    footerTitle: 'Edunet Kenya',
    address: 'Nairobi, Kenya',
    email: 'info@edunet.ke',
    // the number edunet.ke itself links to
    whatsapp: 'https://wa.me/254700000000',
    copyrightName: 'Edunet Kenya',
    // the Drupal system path: the Swahili page has no "content/privacy-policy" alias
    privacyPath: 'node/3',
    website: { label: 'edunet.ke', href: 'https://edunet.ke' },
    hasTalentsPage: false,
    germanCoursesFirst: true,
    // edunet.ke links are green (--edu-primary), its accent red is our portal colour
    linkColor: '#0c680c',
    hasLegalNotice: false,
  },
  edunetma: {
    site: 'https://edunet.co.ma',
    languages: ['en', 'de', 'ar', 'fr'],
    texts: edunetmaTexts,
    // edunet.co.ma uses the same logo file as edunet.ke
    // eslint-disable-next-line global-require
    logo: require('@/assets/img/logo/logo-edunetke.png'),
    footerTitle: 'Edunet Morocco',
    address: 'Luisenstr. 41, 10117 Berlin, Germany',
    email: 'info@edunet.co.ma',
    whatsapp: 'https://wa.me/4915563379350',
    // edunet.co.ma itself prints "© Edunet Kenya" (copied from edunet.ke) — the Moroccan name here
    copyrightName: 'Edunet Morocco',
    // the Drupal system path: the French and Arabic pages have no "content/privacy-policy" alias
    privacyPath: 'node/3',
    // edunet.co.ma shows the number in the WhatsApp row, no separate phone row
    whatsappLabel: '+49 155 6337 9350',
    website: { label: 'edunet.co.ma', href: 'https://edunet.co.ma/' },
    linkedin: 'https://www.linkedin.com/company/edunet-gmbh',
    hasTalentsPage: false,
    // edunet.co.ma footer icons are green, its accent red is our portal colour
    iconColor: '#186a3e',
    hasLegalNotice: false,
  },
};

export const edunetBrand = (sid: string): EdunetBrand => edunetBrands[sid] || edunetBrands.edunet;

export const brandLanguage = (brand: EdunetBrand, lang: string): string => (brand.languages.includes(lang) ? lang : 'en');

export const brandUrl = (brand: EdunetBrand, lang: string, path: string): string => `${brand.site}/${lang}/${path}`;

// Talentspace content of the edunet brands: the partner card's "Partnerschaft anfragen" e-mail
export const edunetTalentspace: SiteTalentspaceContent = {
  partnerEmail: 'jm@edunet.works',
};

// edunet.in and edunet.ke use the same contact as edunet: the partner card's text names jm@edunet.works
export const edunetinTalentspace: SiteTalentspaceContent = {
  partnerEmail: 'jm@edunet.works',
};
